import React, { useEffect, useState } from "react";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { Button, Col, Row, Form, Space, Input, Divider, Checkbox } from "antd";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as Strings from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import styles from "../pages/RentManagement/ReservationManagement.module.css";
import moment from "moment";
import ModalChangeRentCar from "./ModalChangeRentCar";
import ModalAddPaidItems from "./ModalAddPaidItems";

function RentDetail({
    rent,
    onUseRent,
    onUseRentCancel,
    onArrivedRent,
    onCancelRent,
    onChangeRentCar,
    onChangeRentDetail,
    onUpdatePaidItem,
    loadingAddPaidItem,
}: {
    rent: Types.RentDto;
    onUseRent?: (rent: Types.RentDto) => void;
    onUseRentCancel?: (rent: Types.RentDto) => void;
    onArrivedRent?: (rent: Types.RentDto) => void;
    onCancelRent?: (rent: Types.RentDto) => void;
    onChangeRentCar?: (rent: Types.RentDto, rentCarId: number) => void;
    onChangeRentDetail?: (rent: Types.RentDto, memo: string, checkedUsagePolicy: boolean, checkedExitPolicy: boolean) => void;
    onUpdatePaidItem?: (rent: Types.RentDto, paidItemIds: Array<number>, payments?: Array<Types.RentPaidItemDto>) => void;
    loadingAddPaidItem?: boolean;
}) {
    const [inputType, setInputType] = useState<boolean>(false);
    const [form] = Form.useForm();

    const [strDelayedTime, setStrDelayedTime] = useState<string | undefined>();
    const [changableRentCarList, setChangableRentCarList] = useState<Array<Types.RentCarDto>>([]);
    const [openModalChangeRentCar, setOpenModalChangeRentCar] = useState(false);
    const [tempMemo, setTempMemo] = useState("");
    const [tempCheckedUsagePolicy, setTempCheckedUsagePolicy] = useState<boolean>(false);
    const [tempCheckedExitPolicy, setTempCheckedExitPolicy] = useState<boolean>(false);
    const [openModalAddPaidItem, setOpenModalAddPaidItem] = useState(false);

    useEffect(() => {
        if (rent) {
            console.log("rent", rent);
            form.setFieldsValue(rent);

            setDelayedTime(rent);
            setTempMemo(rent.memo ? rent.memo : "");
            setTempCheckedUsagePolicy(rent.checkedUsagePolicy ?? false);
            setTempCheckedExitPolicy(rent.checkedExitPolicy ?? false);
        }
    }, [rent]);

    const setDelayedTime = (rentData: Types.RentDto) => {
        if (rentData.arrivedDate === null || rentData.arrivedDate === undefined) {
            setStrDelayedTime(undefined);
            return;
        }

        const originalArrivedDate = moment(rentData.endDate + Common.DEFAULT_ARRIVED_TIME);
        const realArrivedDate = moment(Utils.utcToLocalTime(rent.arrivedDate, Common.FORMAT_DATE_TIME));

        const hour = realArrivedDate.diff(originalArrivedDate, "hours");
        const minute = realArrivedDate.diff(originalArrivedDate, "minutes") % 60;

        if (minute > 0 || hour > 0) {
            setStrDelayedTime("지연 " + (hour > 0 ? String(hour) + "시간 " : "") + (minute > 0 ? String(minute) + "분" : ""));
        } else {
            setStrDelayedTime(undefined);
        }
    };

    const changeRentCar = () => {
        setOpenModalChangeRentCar(true);
        requestGetRentCars();
    };

    const requestAxiosGetRentCars = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_CARS_ALL_URL, {
            params: {
                rentRegionId: rent.rentCar.rentRegion.rentRegionId,
            },
        });
        return response.data;
    };

    const {
        loading: loadingGetRentCars,
        error: errorGetRentCars,
        data: resultGetRentCars,
        execute: requestGetRentCars,
    } = useAsyncAxios(requestAxiosGetRentCars);

    useEffect(() => {
        if (resultGetRentCars === null) return;
        console.log("resultGetRentCars", resultGetRentCars);

        setChangableRentCarList(resultGetRentCars.rentCars);
    }, [resultGetRentCars]);

    useEffect(() => {
        if (errorGetRentCars === null) return;
        console.log("errorGetRentCars", errorGetRentCars);
    }, [errorGetRentCars]);

    const onAddPaidItems = () => {
        setOpenModalAddPaidItem(true);
    };

    useEffect(() => {
        if (!loadingAddPaidItem) {
            setOpenModalAddPaidItem(false);
        }
    }, [loadingAddPaidItem]);

    return (
        <>
            <div>
                <Row justify={"space-between"}>
                    <Col span={6}>
                        <Divider className={styles.subTitle} orientation="left">
                            <span>예약 상세보기</span>
                        </Divider>
                    </Col>
                    <Col style={{ alignContent: "center" }}>
                        {inputType && (
                            <Button
                                onClick={() => {
                                    setInputType(false);
                                    setTempMemo(rent.memo ? rent.memo : "");
                                    setTempCheckedUsagePolicy(rent.checkedUsagePolicy ?? false);
                                    setTempCheckedExitPolicy(rent.checkedExitPolicy ?? false);
                                    form.setFieldValue("memo", rent.memo ? rent.memo : "");
                                }}
                            >
                                취소
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                if (inputType) {
                                    onChangeRentDetail && onChangeRentDetail(rent, tempMemo, tempCheckedUsagePolicy, tempCheckedExitPolicy);
                                }
                                setInputType(!inputType);
                            }}
                        >
                            {inputType ? "저장" : "수정"}
                        </Button>
                    </Col>
                </Row>

                <Form form={form} layout="inline">
                    <Row style={{ width: "100%" }}>
                        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                            <Form.Item className="searchForm" name="createdDate" label="예약일시">
                                <span>{rent.createdDate ? Utils.utcToLocalTime(rent.createdDate, Common.FORMAT_DATE_TIME) : Strings.dash}</span>
                            </Form.Item>
                            <Form.Item className="searchForm" name="rentNo" label="예약번호">
                                <span>{rent.rentNo ? rent.rentNo : Strings.dash}</span>
                            </Form.Item>
                            <Form.Item className="searchForm" label="예약취소일">
                                {rent.canceledDate ? (
                                    <span>{Utils.utcToLocalTime(rent.canceledDate, Common.FORMAT_DATE_TIME)}</span>
                                ) : (
                                    <Button danger size="small" onClick={() => rent && onCancelRent && onCancelRent(rent)}>
                                        예약취소
                                    </Button>
                                )}
                            </Form.Item>
                            <Form.Item className="searchForm" name="startDate" label="이용일">
                                <span>{rent.startDate ? rent.startDate : Strings.dash}</span>
                            </Form.Item>
                            <Form.Item className="searchForm" name="endDate" label="반납일">
                                <span>{rent.endDate ? rent.endDate : Strings.dash}</span>
                            </Form.Item>
                            <Form.Item className="searchForm" name={["rentCar", "car", "carType", "typeName"]} label="차량타입">
                                <span>{rent.rentCar?.car?.carType?.typeName ? rent.rentCar?.car?.carType?.typeName : Strings.dash}</span>
                            </Form.Item>
                            <Form.Item className="searchForm" name={["rentCar", "car", "carNumber"]} label="차량번호">
                                <Row justify="space-between">
                                    <span>{rent.rentCar?.car?.carNumber ? rent.rentCar?.car?.carNumber : Strings.dash}</span>
                                    <Button size="small" onClick={changeRentCar} disabled={rent.usedDate !== null}>
                                        차량변경
                                    </Button>
                                </Row>
                            </Form.Item>
                            <Form.Item className="searchForm" name="checkedUsagePolicy" label="위약금 및 이용 안내 확인">
                                <Checkbox
                                    disabled={!inputType}
                                    checked={tempCheckedUsagePolicy}
                                    onChange={(e) => setTempCheckedUsagePolicy(e.target.checked)}
                                >
                                    <span style={{ color: tempCheckedUsagePolicy ? "" : "red" }}>{` 사전 체크 ${
                                        tempCheckedUsagePolicy ? "완료" : "미완료"
                                    } 상태`}</span>
                                </Checkbox>
                            </Form.Item>
                            <Form.Item className="searchForm searchLastRow" name="checkedExitPolicy" label="출차 확인">
                                <Checkbox
                                    disabled={!inputType}
                                    checked={tempCheckedExitPolicy}
                                    onChange={(e) => setTempCheckedExitPolicy(e.target.checked)}
                                >
                                    <span style={{ color: tempCheckedExitPolicy ? "" : "red" }}>
                                        {` 사전 체크 ${tempCheckedExitPolicy ? "완료" : "미완료"} 상태`}
                                    </span>
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                            <Form.Item className="searchForm" name="status" label="예약상태">
                                {rent ? (
                                    rent.rentStatus === "DURING_PAYMENT" ? (
                                        <span style={{ color: "red" }}>{Utils.getRentStatus(rent)}</span>
                                    ) : (
                                        <span>{Utils.getRentStatus(rent)}</span>
                                    )
                                ) : (
                                    <span>{Strings.dash}</span>
                                )}
                            </Form.Item>
                            <Form.Item className="searchForm" label="출차시간">
                                {rent.usedDate ? (
                                    <Space>
                                        <span>{Utils.utcToLocalTime(rent.usedDate, Common.FORMAT_DATE_TIME)}</span>
                                        <Button danger size="small" onClick={() => rent && onUseRentCancel && onUseRentCancel(rent)}>
                                            출차 취소
                                        </Button>
                                    </Space>
                                ) : (
                                    <Button
                                        disabled={rent.startDate <= moment(new Date()).format(Common.FORMAT_DATE) ? false : true}
                                        size="small"
                                        onClick={() => rent && onUseRent && onUseRent(rent)}
                                    >
                                        출차
                                    </Button>
                                )}
                            </Form.Item>
                            <Form.Item className="searchForm" label="반납시간">
                                {rent.arrivedDate ? (
                                    <span>
                                        {Utils.utcToLocalTime(rent.arrivedDate, Common.FORMAT_DATE_TIME)} {strDelayedTime && strDelayedTime}
                                    </span>
                                ) : (
                                    <Button size="small" onClick={() => rent && onArrivedRent && onArrivedRent(rent)}>
                                        반납
                                    </Button>
                                )}
                            </Form.Item>
                            <Form.Item className="searchForm" label="유료대여">
                                <Row justify="space-between">
                                    <Space direction="vertical" size={0}>
                                        {rent.rentPaidItems.length > 0 ? (
                                            rent.rentPaidItems.map((item: Types.RentPaidItemDto) => {
                                                const createdDate = rent.rentPayments.find(
                                                    (pay) => pay?.rentPaymentItem?.rentPaymentItemName === item.rentPaidItemName
                                                )?.createdDate;
                                                return (
                                                    <span
                                                        key={item.rentPaidItemId}
                                                        style={{ whiteSpace: "pre-wrap" }}
                                                        className={createdDate && moment(createdDate).isAfter(moment(rent.createdDate)) ? styles.paidItems : ""}
                                                    >
                                                        {item.rentPaidItemName}
                                                    </span>
                                                );
                                            })
                                        ) : (
                                            <span style={{ fontSize: 16 }}>-</span>
                                        )}
                                    </Space>
                                    <Button size="small" onClick={onAddPaidItems}>
                                        {rent.rentPaidItems === null || rent.rentPaidItems.length === 0 ? "추가" : "변경"}
                                    </Button>
                                </Row>
                            </Form.Item>
                            <Form.Item className="searchForm  searchLastRow" name="memo" label="기타">
                                {/** TODO: 크기 변경 */}
                                {inputType ? (
                                    <Input.TextArea
                                        autoSize={{ maxRows: 7, minRows: 7 }}
                                        placeholder="렌트 관련 메모 입력란입니다."
                                        value={tempMemo}
                                        onChange={(e) => setTempMemo(e.target.value)}
                                    />
                                ) : (
                                    <Input.TextArea autoSize={{ maxRows: 7, minRows: 7 }}>{tempMemo}</Input.TextArea>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>

            {openModalChangeRentCar && (
                <ModalChangeRentCar
                    rentCars={changableRentCarList}
                    open={openModalChangeRentCar}
                    onChangedOpen={(open: boolean) => {
                        setOpenModalChangeRentCar(open);
                    }}
                    onChangedRentCar={(rentCarId) => {
                        onChangeRentCar && onChangeRentCar(rent, rentCarId);
                        setOpenModalChangeRentCar(false);
                    }}
                />
            )}

            <ModalAddPaidItems
                paidItems={rent.rentPaidItems}
                open={openModalAddPaidItem}
                onChangedOpen={(open: boolean) => {
                    setOpenModalAddPaidItem(open);
                }}
                onAddPaidItems={(paidItems, payments) => {
                    setOpenModalAddPaidItem(false);
                    onUpdatePaidItem &&
                        onUpdatePaidItem(
                            rent,
                            paidItems.map((paidItem) => paidItem.rentPaidItemId),
                            payments
                        );
                }}
                loadingAddPaidItem={loadingAddPaidItem}
            />
        </>
    );
}

export default RentDetail;
